/**
 * Shared rules for the "custom size" inputs on the banner ads and image
 * generation pages.
 *
 * Both pages used to clamp silently (`Math.min(Math.max(w, 256), 2048)`), so a
 * 2400x1256 request was generated as 2048x1256 — wrong on one axis, wrong
 * aspect ratio, and no message shown. Validation now happens up front and out
 * of range values are refused instead of rewritten.
 */

/**
 * Thin in-house banners (newsletter headers such as 730x180) are narrower than
 * 256px on one side. The backend always generates at >= ~1MP and resizes down,
 * so a small final side does not lose sharpness. Keep in sync with
 * ImageGenerationJobController.
 */
export const CUSTOM_IMAGE_SIZE_MIN = 64;

/**
 * gpt-image-2.5's largest side. The backend generates at or above the requested
 * size and resizes down afterwards, so anything inside this side and
 * {@link CUSTOM_IMAGE_SIZE_MAX_PIXELS} stays sharp. Keep in sync with the
 * `target_width` / `target_height` rules in ImageGenerationJobController.
 */
export const CUSTOM_IMAGE_SIZE_MAX = 3840;

/**
 * gpt-image-2.5's pixel ceiling (3840x2160). A larger area would be drawn
 * smaller and upscaled, so the backend refuses it. Keep in sync with
 * ImageGenerationJobController.
 */
export const CUSTOM_IMAGE_SIZE_MAX_PIXELS = 8_294_400;

/**
 * Longest-to-shortest side limit. The image models draw at most 3:1 (8:1 for
 * Nano Banana 2.1); wider banners get their margins painted or stretched from
 * the edges, past 8:1 after a relayout to 8:1. At 24:1 the 8:1 banner still
 * fills a third of the width. Keep in sync with ImageGenerationJobController.
 */
export const CUSTOM_IMAGE_SIZE_MAX_RATIO = 24;

/** 広告バナー・画像生成のカスタムサイズ入力欄の下に出す案内 */
export const CUSTOM_IMAGE_SIZE_HINT =
  '指定のサイズを入力してください。（推奨は256〜2560pxです。縦横比が特殊な場合、AIの仕様により失敗可能性が高まります。）';

const DEFAULT_SIZE = 1024;

const parseSide = (value: string): number | null => {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }

  return Number(trimmed);
};

/**
 * Returns a message describing why the size cannot be used, or null when it is
 * valid.
 */
export const validateCustomImageSize = (width: string, height: string): string | null => {
  const parsedWidth = parseSide(width);
  const parsedHeight = parseSide(height);

  if (parsedWidth === null || parsedHeight === null) {
    return `横幅・縦幅は${CUSTOM_IMAGE_SIZE_MIN}〜${CUSTOM_IMAGE_SIZE_MAX}pxの整数で入力してください`;
  }

  const outOfRange = [parsedWidth, parsedHeight].some(
    (side) => side < CUSTOM_IMAGE_SIZE_MIN || side > CUSTOM_IMAGE_SIZE_MAX,
  );

  if (outOfRange) {
    return `横幅・縦幅は${CUSTOM_IMAGE_SIZE_MIN}〜${CUSTOM_IMAGE_SIZE_MAX}pxで入力してください（指定: ${width.trim()}×${height.trim()}）`;
  }

  if (Math.max(parsedWidth, parsedHeight) > CUSTOM_IMAGE_SIZE_MAX_RATIO * Math.min(parsedWidth, parsedHeight)) {
    return `縦横比は${CUSTOM_IMAGE_SIZE_MAX_RATIO}:1（1:${CUSTOM_IMAGE_SIZE_MAX_RATIO}）までで入力してください（指定: ${width.trim()}×${height.trim()}）`;
  }

  if (parsedWidth * parsedHeight > CUSTOM_IMAGE_SIZE_MAX_PIXELS) {
    return `横幅×縦幅は${CUSTOM_IMAGE_SIZE_MAX_PIXELS.toLocaleString('ja-JP')}px（3840×2160）までで入力してください（指定: ${width.trim()}×${height.trim()}）`;
  }

  return null;
};

/**
 * Builds the "WxH" string sent to the API. Values are used as entered — call
 * {@link validateCustomImageSize} first and block the request on failure.
 */
export const formatCustomImageSize = (width: string, height: string): string => {
  const parsedWidth = parseSide(width) ?? DEFAULT_SIZE;
  const parsedHeight = parseSide(height) ?? DEFAULT_SIZE;

  return `${parsedWidth}x${parsedHeight}`;
};
